import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { SmartphoneIcon, GlobeIcon, MailIcon, FileTextIcon, MicIcon, ListIcon, SparklesIcon, LockIcon, CalendarIcon, TruckIcon, MapPinIcon, MessagesSquareIcon, ChevronRightIcon, DownloadIcon, ArrowRightIcon, ArrowDownIcon, PlayIcon, DatabaseIcon, UsersIcon, BarChart3Icon, MessageSquareIcon } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { trackEvent } from '../../utils/analytics';
import { TruckRouteMap, DropOffMap, VoiceWave, InboxPreview } from './CollectMiniVisuals';
type Bilingual = {
  en: string;
  fr: string;
};
const EASE = [0.23, 1, 0.32, 1] as const;
const PHOTO_APP = 'https://cdn.magicpatterns.com/patterns/generated-images/582b7e04-d387-438d-966a-c14bfd02dc8d.jpg';
const PHOTO_WEB = 'https://cdn.magicpatterns.com/patterns/generated-images/10b44c47-e46d-4448-a584-fc8d0788f1e9.jpg';
const PHOTO_FORM = 'https://cdn.magicpatterns.com/patterns/generated-images/57c75f66-e81c-4763-99b6-cffaef4eb83d.jpg';
const PHOTO_CONTAINER = 'https://cdn.magicpatterns.com/patterns/generated-images/42283f42-4328-471a-814a-38b21dee0cd6.jpg';
const PHOTO_TAKEOUT = 'https://cdn.magicpatterns.com/patterns/generated-images/5cac7584-1c36-4989-8a52-96f01ec3666e.jpg';
type Thumb = {
  kind: 'photo';
  src: string;
  alt: Bilingual;
} | {
  kind: 'inbox';
} | {
  kind: 'voice';
};
const INPUTS: {
  icon: typeof SmartphoneIcon;
  title: Bilingual;
  sub: Bilingual;
  thumb: Thumb;
}[] = [{
  icon: SmartphoneIcon,
  title: {
    en: 'Mobile app',
    fr: 'Application mobile'
  },
  sub: {
    en: 'Questions, photos, reminders',
    fr: 'Questions, photos, rappels'
  },
  thumb: {
    kind: 'photo',
    src: PHOTO_APP,
    alt: {
      en: 'A hand holding a smartphone with a municipal app.',
      fr: 'Une main tenant un téléphone avec une application municipale.'
    }
  }
}, {
  icon: GlobeIcon,
  title: {
    en: 'Website / portal',
    fr: 'Site web / portail'
  },
  sub: {
    en: 'Searches, forms',
    fr: 'Recherches, formulaires'
  },
  thumb: {
    kind: 'photo',
    src: PHOTO_WEB,
    alt: {
      en: 'A municipal website on a laptop.',
      fr: 'Un site municipal sur un portable.'
    }
  }
}, {
  icon: MailIcon,
  title: {
    en: 'Email',
    fr: 'Courriel'
  },
  sub: {
    en: 'Requests, reports',
    fr: 'Demandes, signalements'
  },
  thumb: {
    kind: 'inbox'
  }
}, {
  icon: FileTextIcon,
  title: {
    en: 'Forms',
    fr: 'Formulaires'
  },
  sub: {
    en: 'Online or on site',
    fr: 'En ligne ou sur place'
  },
  thumb: {
    kind: 'photo',
    src: PHOTO_FORM,
    alt: {
      en: 'An online form with fields.',
      fr: 'Un formulaire en ligne avec ses champs.'
    }
  }
}, {
  icon: MicIcon,
  title: {
    en: 'Voice',
    fr: 'Voix'
  },
  sub: {
    en: 'Spoken questions',
    fr: 'Questions orales'
  },
  thumb: {
    kind: 'voice'
  }
}];
const EXAMPLES: {
  quote: Bilingual;
  mode: Bilingual;
  lead: 'photo' | 'text' | 'voice';
}[] = [{
  quote: {
    en: '“Where does this container go?”',
    fr: '« Où ça va, ce contenant ? »'
  },
  mode: {
    en: 'Photo',
    fr: 'Photo'
  },
  lead: 'photo'
}, {
  quote: {
    en: '“When is garbage collected at my address?”',
    fr: '« Quand passent les ordures à mon adresse ? »'
  },
  mode: {
    en: 'Text',
    fr: 'Texte'
  },
  lead: 'text'
}, {
  quote: {
    en: '“Is there a special collection this week?”',
    fr: '« Y a-t-il une collecte spéciale cette semaine ? »'
  },
  mode: {
    en: 'Voice',
    fr: 'Voix'
  },
  lead: 'voice'
}];
const TRAITS: Bilingual[] = [{
  en: 'Understands',
  fr: 'Comprend'
}, {
  en: 'Contextualizes',
  fr: 'Contextualise'
}, {
  en: 'Answers',
  fr: 'Répond'
}, {
  en: 'Guides',
  fr: 'Oriente'
}];
const BENEFITS: {
  icon: typeof UsersIcon;
  title: Bilingual;
  sub: Bilingual;
}[] = [{
  icon: MessageSquareIcon,
  title: {
    en: 'Every request, one entry point',
    fr: 'Toutes les demandes, un seul point d’entrée'
  },
  sub: {
    en: 'Web, mobile, email, form or voice.',
    fr: 'Web, mobile, courriel, formulaire ou voix.'
  }
}, {
  icon: DatabaseIcon,
  title: {
    en: 'Reliable, contextualized answers',
    fr: 'Des réponses fiables et contextualisées'
  },
  sub: {
    en: 'Based on your municipality, address and available services.',
    fr: 'Selon votre municipalité, votre adresse et les services disponibles.'
  }
}, {
  icon: UsersIcon,
  title: {
    en: 'A simple resident experience',
    fr: 'Une expérience simple pour les citoyens'
  },
  sub: {
    en: 'Less searching. More answers.',
    fr: 'Moins de recherches, plus de réponses.'
  }
}, {
  icon: BarChart3Icon,
  title: {
    en: 'Useful data for your organization',
    fr: 'Des données utiles pour votre organisation'
  },
  sub: {
    en: 'To better inform, plan and respond.',
    fr: 'Pour mieux informer, planifier et réagir.'
  }
}];
function Badge({
  children
}: {
  children: React.ReactNode;
}) {
  return <span className="rounded-md bg-[#F1F1FB] px-2 py-[3px] font-sans text-[9.5px] font-semibold tracking-[0.14em] uppercase text-[#4F46E5] whitespace-nowrap" data-id="element-1748" data-mp="wj9exk3a777w">
      {children}
    </span>;
}
function IconTile({
  icon: Icon
}: {
  icon: typeof SmartphoneIcon;
}) {
  return <span className="w-10 h-10 shrink-0 rounded-xl bg-[#F4F4FC] flex items-center justify-center" data-id="element-1749" data-mp="1kfn9hxf3csi4">
      <Icon className="w-[18px] h-[18px] text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-1750" data-mp="1fhltxmgvjbbe" />
    </span>;
}
export function CollectHero() {
  const {
    lang
  } = useLanguage();
  const isFR = lang === 'FR';
  const pick = (value: Bilingual) => isFR ? value.fr : value.en;
  const fade = (delay: number) => ({
    initial: {
      opacity: 0,
      y: 18
    },
    animate: {
      opacity: 1,
      y: 0
    },
    transition: {
      duration: 0.55,
      delay,
      ease: EASE
    }
  });
  const renderThumb = (thumb: Thumb) => {
    if (thumb.kind === 'inbox') return <InboxPreview data-id="element-1751" data-mp="vv8b78v6sabn" />;
    if (thumb.kind === 'voice') return <VoiceWave data-id="element-1752" data-mp="10t9u9tsm49n4" />;
    return <img src={thumb.src} alt={pick(thumb.alt)} loading="lazy" className="w-[78px] h-[54px] shrink-0 rounded-lg object-cover border border-[#EDEEF3]" data-id="element-1753" data-mp="10sgy1mra52j8" />;
  };
  return <div className="max-w-[1480px] mx-auto" data-id="element-1754" data-mp="19mls0f1pmu2z">
      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,0.95fr)_minmax(0,0.9fr)_minmax(0,0.7fr)_minmax(0,1.2fr)] gap-y-10 xl:gap-x-4 items-center" data-id="element-1755" data-mp="19netp1ltate9">
        {/* MESSAGE */}
        <motion.div {...fade(0)} className="xl:pr-6" data-id="element-1756" data-mp="1jhwzccasgz0p">
          <p className="font-sans text-xs font-semibold tracking-[0.22em] uppercase text-[#4F46E5]" data-id="element-1757" data-mp="1oib5fostxyw3">
            {isFR ? 'Collectes ATNA' : 'ATNA Waste Collection'}
          </p>
          <h1 className="font-serif text-5xl lg:text-[3.4rem] text-[#121212] leading-[1.06] tracking-tight mt-6" data-id="element-1758" data-mp="3qa7lnbpxzwx7">
            {isFR ? <>
                De la question
                <span className="block" data-id="element-1759" data-mp="3udm8omcatdk5">à la bonne réponse.</span>
              </> : <>
                From the question
                <span className="block" data-id="element-1760" data-mp="3i0s0iyudkguu">to the right answer.</span>
              </>}
          </h1>
          <p className="font-serif text-2xl lg:text-[1.75rem] text-[#9AA0AE] leading-snug mt-5" data-id="element-1761" data-mp="3d307u2lwoqpl">
            {isFR ? <>
                Où ça va. Quand ça passe.
                <span className="block" data-id="element-1762" data-mp="9kp11gdf1yhl">Que faire ensuite.</span>
              </> : <>
                Where it goes. When it’s collected.
                <span className="block" data-id="element-1763" data-mp="ktr1evlk9j5">What to do next.</span>
              </>}
          </p>
          <p className="font-sans text-[17px] text-[#5B6478] leading-relaxed mt-6 max-w-xl" data-id="element-1764" data-mp="3rrwgo1vse7r9">
            {isFR ? 'Collectes ATNA capte toutes les demandes liées aux matières résiduelles — sur le web, dans l’application mobile, par courriel, par formulaire, par photo ou de vive voix — et y répond avec des informations fiables, adaptées à votre municipalité.' : 'ATNA Waste Collection captures every request related to waste collection services — through the web, mobile app, email, forms, photos or voice — and provides reliable answers adapted to each municipality.'}
          </p>

          <div className="flex flex-col items-start gap-6 mt-9" data-id="element-1765" data-mp="f96zu02dj92z">
            <Link to="/request-demo" onClick={() => trackEvent('Demo CTA Clicked', {
            location: 'Collect Hero'
          })} className="inline-flex items-center gap-2 px-7 py-3.5 bg-[#4F46E5] text-white text-[15px] font-medium rounded-lg hover:bg-[#4F46E5]/90 transition-colors" data-id="element-1766" data-mp="12zkorhpnwrtc">
              {isFR ? 'Découvrir Collectes ATNA' : 'Discover ATNA Waste Collection'}
              <ArrowRightIcon className="w-4 h-4" strokeWidth={2} aria-hidden="true" data-id="element-1767" data-mp="1lyox0o9dqhjy" />
            </Link>
            <Link to="/demo" onClick={() => trackEvent('Proof CTA Clicked', {
            location: 'Collect Hero'
          })} className="inline-flex items-center gap-3 group" data-id="element-1768" data-mp="1qpngtxtktxry">
              <span className="w-11 h-11 rounded-full border border-[#D9D8F7] flex items-center justify-center transition-colors group-hover:border-[#4F46E5]" data-id="element-1769" data-mp="36h96y6nquise">
                <PlayIcon className="w-3.5 h-3.5 text-[#4F46E5] fill-[#4F46E5]" aria-hidden="true" data-id="element-1770" data-mp="3k9p6uylcm0p3" />
              </span>
              <span data-id="element-1771" data-mp="2sg6ixod6uyeu">
                <span className="block font-sans text-[15px] font-medium text-[#121212]" data-id="element-1772" data-mp="2shpytduxn7g3">
                  {isFR ? 'Voir en action' : 'See it in action'}
                </span>
                <span className="block font-sans text-[13px] text-[#9AA0AE]" data-id="element-1773" data-mp="31hwacu72lxcm">2 min</span>
              </span>
            </Link>
          </div>
        </motion.div>

        {/* INPUTS */}
        <motion.div {...fade(0.08)} className="space-y-2.5" data-id="element-1774" data-mp="gqvswkonr9ue">
          <ul className="space-y-2.5" data-id="element-1775" data-mp="gpaz4xa63uxl">
            {INPUTS.map(({
            icon,
            title,
            sub,
            thumb
          }) => <li key={title.en} className="flex items-center gap-3 rounded-xl border border-[#E7E8F0] bg-white pl-3 pr-2 py-2 shadow-[0_1px_4px_rgba(18,18,18,0.03)]" data-id="element-1776" data-mp="brszxylycu2s">
                <IconTile icon={icon} data-id="element-1777" data-mp="3sa5r31akqa6x" />
                <span className="min-w-0 flex-1" data-id="element-1778" data-mp="3ngbaw7u6gdcl">
                  <span className="block font-sans text-[14px] font-medium text-[#121212] leading-snug" data-id="element-1779" data-mp="3nf04e09ckrhs">
                    {pick(title)}
                  </span>
                  <span className="block font-sans text-[12px] text-[#9AA0AE] leading-snug mt-0.5" data-id="element-1780" data-mp="3eh7puybg9nvg">
                    {pick(sub)}
                  </span>
                </span>
                <span className="hidden sm:block" data-id="element-1781" data-mp="34hgu6tb5l2ts">{renderThumb(thumb)}</span>
              </li>)}
          </ul>

          <div className="rounded-2xl border border-[#E7E8F0] bg-[#FAFAFC] p-3 mt-4" data-id="element-1782" data-mp="30hyk6skvec5q">
            <p className="font-sans text-[10.5px] font-semibold tracking-[0.18em] uppercase text-[#5B6478] px-1 pt-1" data-id="element-1783" data-mp="12pualctlca7c">
              {isFR ? 'Exemples de demandes' : 'Examples of requests'}
            </p>
            <ul className="space-y-2 mt-3" data-id="element-1784" data-mp="2gm41bhg8dd41">
              {EXAMPLES.map(({
              quote,
              mode,
              lead
            }) => <li key={quote.en} className="flex items-center gap-3 rounded-xl border border-[#EDEEF3] bg-white px-2.5 py-2" data-id="element-1785" data-mp="2lkp1jgvhfwsh">
                  {lead === 'photo' ? <img src={PHOTO_TAKEOUT} alt={isFR ? 'Photo d’un contenant en plastique.' : 'Photo of a plastic container.'} loading="lazy" className="w-14 h-10 shrink-0 rounded-md object-cover" data-id="element-1786" data-mp="2ugej1ett870l" /> : <span className="w-10 h-10 shrink-0 rounded-lg bg-[#F4F4FC] flex items-center justify-center" data-id="element-1787" data-mp="2bnkfdh1n01bo">
                      {lead === 'text' ? <ListIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-1788" data-mp="3i3mqa2astq59" /> : <MicIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-1789" data-mp="1n0g6wu33jrar" />}
                    </span>}
                  <span className="min-w-0" data-id="element-1790" data-mp="1j17hu2j04hlj">
                    <span className="block font-sans text-[13px] text-[#121212] leading-snug" data-id="element-1791" data-mp="1wz5rwvqahrv8">
                      {pick(quote)}
                    </span>
                    <span className="block font-sans text-[11.5px] text-[#9AA0AE] mt-0.5" data-id="element-1792" data-mp="1rxzy0ukw7yk4">
                      {pick(mode)}
                    </span>
                  </span>
                </li>)}
            </ul>
          </div>
        </motion.div>

        {/* AI COLLECT NODE */}
        <motion.div {...fade(0.14)} className="relative flex flex-col items-center self-stretch justify-center" data-id="element-1793" data-mp="1e1uttege94hd">
          <ArrowDownIcon className="xl:hidden w-5 h-5 text-[#C7C4F2] mb-4" strokeWidth={1.8} aria-hidden="true" data-id="element-1794" data-mp="ljqs7k6p4lwf" />

          {/* Connectors: in from inputs, out toward answers */}
          <svg className="hidden xl:block absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none" fill="none" aria-hidden="true" data-id="element-1795" data-mp="gijkfl4jzmpr">
            {[10, 24, 38, 52, 66, 82].map(y => <path key={`in-${y}`} d={`M0 ${y} C 18 ${y}, 22 50, 38 50`} stroke="#A5A1EE" strokeWidth="1" vectorEffect="non-scaling-stroke" data-id="element-1796" data-mp="2j5hxmsbdtt61" />)}
            {[14, 30, 48, 66, 84].map(y => <path key={`out-${y}`} d={`M62 50 C 78 50, 82 ${y}, 100 ${y}`} stroke="#A5A1EE" strokeWidth="1" vectorEffect="non-scaling-stroke" data-id="element-1797" data-mp="3fv7qbs07c0wn" />)}
          </svg>
          <div className="hidden xl:block absolute left-0 top-0 h-full w-full pointer-events-none" data-id="element-1798" data-mp="1qh2kycfq3mxv">
            {[10, 24, 38, 52, 66, 82].map(y => <span key={y} className="absolute -left-[4px] w-2 h-2 rounded-full bg-[#4F46E5]" style={{
            top: `calc(${y}% - 4px)`
          }} data-id="element-1799" data-mp="2ar3t5sah3zn6" />)}
            {[14, 30, 48, 66, 84].map(y => <span key={y} className="absolute -right-[4px] w-2 h-2 rounded-full bg-[#4F46E5]" style={{
            top: `calc(${y}% - 4px)`
          }} data-id="element-1800" data-mp="211txy3kc20uu" />)}
          </div>

          <div className="relative flex items-center justify-center" data-id="element-1801" data-mp="2or527v0w6ddq">
            <span className="absolute w-[250px] h-[250px] rounded-full bg-[#4F46E5]/[0.05] blur-2xl" aria-hidden="true" data-id="element-1802" data-mp="2orxzm5dynqj7" />
            <span className="absolute w-[226px] h-[226px] rounded-full bg-[#F6F6FD] border border-[#EEEEFB]" aria-hidden="true" data-id="element-1803" data-mp="2kre2v2xauoxl" />
            <div className="relative w-[192px] h-[192px] rounded-full bg-white border border-[#E0DFF8] flex flex-col items-center justify-center shadow-[0_6px_24px_rgba(79,70,229,0.08)]" data-id="element-1804" data-mp="19ao6ebiohbh6">
              <SparklesIcon className="w-6 h-6 text-[#4F46E5]" strokeWidth={1.6} aria-hidden="true" data-id="element-1805" data-mp="3fz6nnkk5jgiy" />
              <p className="font-serif text-[1.35rem] text-[#121212] leading-tight text-center px-5 mt-3" data-id="element-1806" data-mp="3kx84rzy0p5rs">
                {isFR ? 'Collectes ATNA' : 'ATNA Waste Collection'}
              </p>
              <ul className="mt-3.5 space-y-1 text-center" data-id="element-1807" data-mp="3tptd0m40vu2d">
                {TRAITS.map(trait => <li key={trait.en} className="font-sans text-[9.5px] font-semibold tracking-[0.2em] uppercase text-[#5B6478]" data-id="element-1808" data-mp="2k9zddighkq8">
                    {pick(trait)}
                  </li>)}
              </ul>
            </div>
          </div>

          <span className="w-px h-6 border-l border-dashed border-[#C7C4F2] mt-8" aria-hidden="true" data-id="element-1809" data-mp="gjt92aqx144n" />
          <div className="relative flex items-center gap-2.5 rounded-xl border border-[#E7E8F0] bg-white px-4 py-2.5" data-id="element-1810" data-mp="2eacnpzryxl4n">
            <LockIcon className="w-4 h-4 text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-1811" data-mp="2ibeo4jxmg387" />
            <span className="font-sans text-[12px] text-[#5B6478] leading-snug" data-id="element-1812" data-mp="3lmljxr5pnk29">
              {isFR ? <>
                  Données municipales
                  <span className="block" data-id="element-1813" data-mp="3un05mrml716a">fiables et à jour.</span>
                </> : <>
                  Reliable and up-to-date
                  <span className="block" data-id="element-1814" data-mp="3qn9h6ukj8x4x">municipal data.</span>
                </>}
            </span>
          </div>

          <ArrowDownIcon className="xl:hidden w-5 h-5 text-[#C7C4F2] mt-6" strokeWidth={1.8} aria-hidden="true" data-id="element-1815" data-mp="3gp64zx868h2" />
        </motion.div>

        {/* ANSWERS */}
        <motion.div {...fade(0.2)} className="rounded-3xl border border-[#E7E8F0] bg-[#FCFCFE] p-4 lg:p-5" data-id="element-1816" data-mp="i76cmxopvokt">
          <p className="font-sans text-[11px] font-semibold tracking-[0.2em] uppercase text-[#4F46E5] px-1" data-id="element-1817" data-mp="r58hxpfhh3dw">
            {isFR ? 'Une réponse claire et utile' : 'A clear, useful answer'}
          </p>

          <ul className="space-y-3 mt-4" data-id="element-1818" data-mp="15s76ncmyhjox">
            {/* 1 — Sorting */}
            <li className="flex items-center gap-4 rounded-2xl border border-[#E7E8F0] bg-white p-3" data-id="element-1819" data-mp="1arjpznemc8mr">
              <img src={PHOTO_CONTAINER} alt={isFR ? 'Un contenant de yogourt vide.' : 'An empty yogurt container.'} loading="lazy" className="w-[88px] h-[66px] shrink-0 rounded-lg object-cover" data-id="element-1820" data-mp="ws2axzr4k9ql" />
              <div className="min-w-0 flex-1" data-id="element-1821" data-mp="mzw8tx75sbxo">
                <div className="flex flex-wrap items-center gap-2" data-id="element-1822" data-mp="rsz367jrfpex">
                  <span className="font-sans text-[13.5px] font-medium text-[#121212]" data-id="element-1823" data-mp="d3t9ez3oyref">
                    {isFR ? 'Tri et matières' : 'Sorting & materials'}
                  </span>
                  <Badge data-id="element-1824" data-mp="i0jmddk796vm">{isFR ? 'Réponse immédiate' : 'Instant answer'}</Badge>
                </div>
                <p className="font-sans text-[13.5px] font-semibold text-[#121212] leading-snug mt-1.5" data-id="element-1825" data-mp="45h8o4942d89">
                  {isFR ? 'Ce contenant va dans le bac de recyclage.' : 'This container goes in the recycling bin.'}
                </p>
                <p className="font-sans text-[12.5px] text-[#9AA0AE] leading-snug mt-0.5" data-id="element-1826" data-mp="jlxn649awi46">
                  {isFR ? 'Videz et rincez-le. Laissez le couvercle attaché.' : 'Empty and rinse it. Leave the lid attached.'}
                </p>
              </div>
              <ChevronRightIcon className="w-4 h-4 shrink-0 text-[#9AA0AE]" aria-hidden="true" data-id="element-1827" data-mp="5mo8j90qtw2b" />
            </li>

            {/* 2 — Calendar */}
            <li className="flex flex-wrap sm:flex-nowrap items-center gap-4 rounded-2xl border border-[#E7E8F0] bg-white p-3" data-id="element-1828" data-mp="3s0eocs0wxs1q">
              <IconTile icon={CalendarIcon} data-id="element-1829" data-mp="3n364mn049fyd" />
              <div className="min-w-0 flex-1" data-id="element-1830" data-mp="3n00f56nksc24">
                <div className="flex flex-wrap items-center gap-2" data-id="element-1831" data-mp="3iz85icask55b">
                  <span className="font-sans text-[13.5px] font-medium text-[#121212]" data-id="element-1832" data-mp="3e1f2fs4sbr99">
                    {isFR ? 'Prochaines collectes' : 'Upcoming collections'}
                  </span>
                  <Badge data-id="element-1833" data-mp="1yi7aoh6ck8x2">{isFR ? 'Calendrier personnalisé' : 'Personalized calendar'}</Badge>
                </div>
                <p className="font-sans text-[13px] text-[#121212] leading-relaxed mt-1.5" data-id="element-1834" data-mp="22jas3841md0e">
                  {isFR ? 'Ordures : jeudi 14 mai' : 'Garbage: Thursday, May 14'}
                  <span className="block" data-id="element-1835" data-mp="27gj8b35ms6z0">
                    {isFR ? 'Recyclage : jeudi 21 mai' : 'Recycling: Thursday, May 21'}
                  </span>
                </p>
              </div>
              <span className="inline-flex items-center gap-2 rounded-lg border border-[#4F46E5] px-3.5 py-2 font-sans text-[12.5px] font-medium text-[#4F46E5] whitespace-nowrap" data-id="element-1836" data-mp="1ervkazthz3hr">
                <DownloadIcon className="w-3.5 h-3.5" strokeWidth={2} aria-hidden="true" data-id="element-1837" data-mp="1olcggah3wuol" />
                {isFR ? 'Ajouter au calendrier' : 'Add to calendar'}
              </span>
            </li>

            {/* 3 — Truck */}
            <li className="flex items-center gap-4 rounded-2xl border border-[#E7E8F0] bg-white p-3" data-id="element-1838" data-mp="3ianj1i0vzz1">
              <IconTile icon={TruckIcon} data-id="element-1839" data-mp="7jvte2w53dzq" />
              <div className="min-w-0 flex-1" data-id="element-1840" data-mp="7ikrk8m98gb9">
                <div className="flex flex-wrap items-center gap-2" data-id="element-1841" data-mp="ciz15v3i7ybe">
                  <span className="font-sans text-[13.5px] font-medium text-[#121212]" data-id="element-1842" data-mp="cjrzc7oedfhx">
                    {isFR ? 'Trajet du camion' : 'Collection truck'}
                  </span>
                  <Badge data-id="element-1843" data-mp="3g10dohjx1x7w">{isFR ? 'Vue en temps réel' : 'Live view'}</Badge>
                </div>
                <p className="font-sans text-[12.5px] text-[#9AA0AE] leading-snug mt-1.5" data-id="element-1844" data-mp="3kzk2cppw9n3y">
                  {isFR ? 'Suivez le camion de collecte dans votre secteur.' : 'Follow the collection truck in your area.'}
                </p>
              </div>
              <span className="hidden sm:block" data-id="element-1845" data-mp="2x86bko7e5zl3">
                <TruckRouteMap data-id="element-1846" data-mp="2x7d93zfgtw8r" />
              </span>
              <ChevronRightIcon className="w-4 h-4 shrink-0 text-[#9AA0AE]" aria-hidden="true" data-id="element-1847" data-mp="326zoofirnokq" />
            </li>

            {/* 4 — Drop-off */}
            <li className="flex items-center gap-4 rounded-2xl border border-[#E7E8F0] bg-white p-3" data-id="element-1848" data-mp="2umf8edliq20c">
              <IconTile icon={MapPinIcon} data-id="element-1849" data-mp="2kqlqsfsxzibp" />
              <div className="min-w-0 flex-1" data-id="element-1850" data-mp="2kpkg4cfu8xui">
                <div className="flex flex-wrap items-center gap-2" data-id="element-1851" data-mp="2kseze0n48cmc">
                  <span className="font-sans text-[13.5px] font-medium text-[#121212]" data-id="element-1852" data-mp="2fqzafxhthlyo">
                    {isFR ? 'Points de dépôt' : 'Drop-off locations'}
                  </span>
                  <Badge data-id="element-1853" data-mp="26yf9urhyt7b7">{isFR ? 'Près de chez vous' : 'Near you'}</Badge>
                </div>
                <p className="font-sans text-[12.5px] text-[#9AA0AE] leading-snug mt-1.5" data-id="element-1854" data-mp="2215njkpvhbrl">
                  {isFR ? 'Trouvez les points de dépôt pour les piles, les vêtements, les appareils électroniques et plus encore.' : 'Find drop-off locations for batteries, clothing, electronics and more.'}
                </p>
              </div>
              <span className="hidden sm:block" data-id="element-1855" data-mp="1deptjh728jl4">
                <DropOffMap data-id="element-1856" data-mp="1ddwlmcwd7o1e" />
              </span>
              <ChevronRightIcon className="w-4 h-4 shrink-0 text-[#9AA0AE]" aria-hidden="true" data-id="element-1857" data-mp="1ieu3eodgqg2d" />
            </li>

            {/* 5 — Other requests */}
            <li className="flex items-center gap-4 rounded-2xl border border-[#E7E8F0] bg-white p-3" data-id="element-1858" data-mp="14gm96o7t9suw">
              <IconTile icon={MessagesSquareIcon} data-id="element-1859" data-mp="19fqirfyndslr" />
              <div className="min-w-0 flex-1" data-id="element-1860" data-mp="1x2ymhtgfgtlo">
                <div className="flex flex-wrap items-center gap-2" data-id="element-1861" data-mp="1x5bbgqkls488">
                  <span className="font-sans text-[13.5px] font-medium text-[#121212]" data-id="element-1862" data-mp="213ikt0cqm39e">
                    {isFR ? 'Autres demandes' : 'Other requests'}
                  </span>
                  <Badge data-id="element-1863" data-mp="1sboxmv2pduau">{isFR ? 'Guidage intelligent' : 'Intelligent guidance'}</Badge>
                </div>
                <p className="font-sans text-[12.5px] text-[#9AA0AE] leading-snug mt-1.5" data-id="element-1864" data-mp="2g11f0i86p8o8">
                  {isFR ? 'Signaler un bac brisé, une collecte manquée ou une situation particulière.' : 'Report a broken bin, missed collection or another specific situation.'}
                </p>
              </div>
              <ChevronRightIcon className="w-4 h-4 shrink-0 text-[#9AA0AE]" aria-hidden="true" data-id="element-1865" data-mp="2q46qpaob3g0e" />
            </li>
          </ul>
        </motion.div>
      </div>

      {/* BENEFITS */}
      <motion.ul {...fade(0.26)} className="mt-14 pt-8 border-t border-[#E7E8F0] grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-6 xl:gap-0" data-id="element-1866" data-mp="2yz37ope9pwyt">
        {BENEFITS.map(({
        icon: Icon,
        title,
        sub
      }, i) => <li key={title.en} className={`flex items-start gap-3.5 xl:px-6 ${i > 0 ? 'xl:border-l xl:border-[#EDEEF3]' : 'xl:pl-0'}`} data-id="element-1867" data-mp="2l6wr5277dnyu" data-mpi={i}>
            <span className="w-11 h-11 shrink-0 rounded-full bg-[#F4F4FC] flex items-center justify-center" data-id="element-1868" data-mp="2g9wo705nh1by">
              <Icon className="w-[18px] h-[18px] text-[#4F46E5]" strokeWidth={1.7} aria-hidden="true" data-id="element-1869" data-mp="iikr2aw3y3l" />
            </span>
            <span className="min-w-0" data-id="element-1870" data-mp="21650gv6hx591">
              <span className="block font-sans text-[14px] font-medium text-[#121212] leading-snug" data-id="element-1871" data-mp="2176l9rdbd63y">
                {pick(title)}
              </span>
              <span className="block font-sans text-[12.5px] text-[#9AA0AE] leading-snug mt-1" data-id="element-1872" data-mp="2b42p3js9p426">
                {pick(sub)}
              </span>
            </span>
          </li>)}
      </motion.ul>
    </div>;
}